import {
  OrderOrigin,
  OrderType
} from "src/core/orders/api/orders.api";
import {
  EndpointConfig,
  RequestConfig,
  UseApiRequest
} from "src/core/shared/api/config.api";
import useAxios from "src/core/shared/hooks/useAxios";
import {
  PaginatedData,
  PaginatedParams
} from "src/library/utils";
import { SupportTicketStatus } from "src/modules/support/api/support.api";

type WidgetDataEndpointConfig = {
  GET_CREDIT_TOP_OFFENDERS: EndpointConfig<
    GetWidgetDataParams<WidgetSlug.CreditTopOffenders>,
    WidgetDataResponse<WidgetSlug.CreditTopOffenders>
  >;
  GET_SUPPORT_TOP_OFFENDERS: EndpointConfig<
    GetWidgetDataParams<WidgetSlug.SupportTopOffenders>,
    WidgetDataResponse<WidgetSlug.SupportTopOffenders>
  >;
  GET_ORDER_TOTALS: EndpointConfig<
    GetWidgetDataParams<WidgetSlug.OrderTotal>,
    WidgetDataResponse<WidgetSlug.OrderTotal>
  >;
  GET_ORDER_TOTALS_BY_ORIGIN: EndpointConfig<
    GetWidgetDataParams<WidgetSlug.OrderTotalByOrigin>,
    WidgetDataResponse<WidgetSlug.OrderTotalByOrigin>
  >;
  GET_ORDER_TOTALS_BY_TYPE: EndpointConfig<
    GetWidgetDataParams<WidgetSlug.OrderTotalByType>,
    WidgetDataResponse<WidgetSlug.OrderTotalByType>
  >;
  GET_TOTAL_TICKETS: EndpointConfig<
    GetWidgetDataParams<WidgetSlug.TotalTickets>,
    WidgetDataResponse<WidgetSlug.TotalTickets>
  >;
  GET_TOTAL_TICKETS_BY_STATUS: EndpointConfig<
    GetWidgetDataParams<WidgetSlug.TotalTicketsByStatus>,
    WidgetDataResponse<WidgetSlug.TotalTicketsByStatus>
  >;
  GET_TOTAL_TICKETS_BY_TYPE: EndpointConfig<
    GetWidgetDataParams<WidgetSlug.TotalTicketsByType>,
    WidgetDataResponse<WidgetSlug.TotalTicketsByType>
  >;
  GET_TOTAL_TIME_SPENT: EndpointConfig<
    GetWidgetDataParams<WidgetSlug.TotalTimeSpent>,
    WidgetDataResponse<WidgetSlug.TotalTimeSpent>
  >;
  GET_PENDING_CUSTOMER_TOTALS: EndpointConfig<
    GetWidgetDataParams<WidgetSlug.PendingCustomer>,
    WidgetDataResponse<WidgetSlug.PendingCustomer>
  >;
};

export enum WidgetSlug {
  /** orders */
  OrderTotal = "order-total",
  OrderTotalByOrigin = "order-total-by-origin",
  OrderTotalByType = "order-total-by-type",
  /** credits */
  CreditTotal = "credit-total",
  CreditTopOffenders = "credit-top-offenders",
  /** support */
  TotalTimeSpent = "total-time-spent",
  TotalTickets = "total-tickets",
  TotalTicketsByType = "total-tickets-by-type",
  TotalTicketsByStatus = "total-tickets-by-status",
  SupportTopOffenders = "support-top-offenders",
  /** general */
  RealTimeData = "real-time-data",
  PendingCustomer = "pending-customer",
  /** accounting */
  AccountingOverview = "accounting-overview"
}

export interface GetWidgetDataParams<T extends WidgetSlug> {
  widgetTypeId: number;
  filters: GetWidgetFiltersParam;
  options: WidgetDataOptions[T];
}

export interface GetWidgetFiltersParam {
  customer?: { id: number };
  branch?: { id: number };
  dateRange: GetWidgetDateRangeParam
}

export interface GetWidgetDateRangeParam {
  start: string;
  end: string;
}

interface WidgetDataOptions {
  /** orders */
  [WidgetSlug.OrderTotal]: null;
  [WidgetSlug.OrderTotalByOrigin]: null;
  [WidgetSlug.OrderTotalByType]: null;
  /** credits */
  [WidgetSlug.CreditTotal]: null;
  [WidgetSlug.CreditTopOffenders]: { pagination: PaginatedParams<CreditTopOffendersData> };
  /** support */
  [WidgetSlug.TotalTimeSpent]: null;
  [WidgetSlug.TotalTickets]: null;
  [WidgetSlug.TotalTicketsByType]: null;
  [WidgetSlug.TotalTicketsByStatus]: null;
  [WidgetSlug.SupportTopOffenders]: { pagination: PaginatedParams<SupportTopOffendersData> };
  /** general */
  [WidgetSlug.RealTimeData]: null;
  [WidgetSlug.PendingCustomer]: null;
  /** accounting */
  [WidgetSlug.AccountingOverview]: null;
}

export interface WidgetDataResponse<T extends WidgetSlug = WidgetSlug> {
  result?: WidgetData[T];
}

export interface WidgetData {
  /** orders */
  [WidgetSlug.OrderTotal]: OrderTotalsResult;
  [WidgetSlug.OrderTotalByOrigin]: OrderTotalByOriginResult;
  [WidgetSlug.OrderTotalByType]: OrderTotalByTypeResult;
  /** credits */
  [WidgetSlug.CreditTotal]: null;
  [WidgetSlug.CreditTopOffenders]: PaginatedData<CreditTopOffendersData>;
  /** support */
  [WidgetSlug.TotalTimeSpent]: TotalTimeSpentResult;
  [WidgetSlug.TotalTickets]: TotalTicketsResult;
  [WidgetSlug.TotalTicketsByType]: TotalTicketsByTypeResult;
  [WidgetSlug.TotalTicketsByStatus]: TotalTicketsByStatusResult;
  [WidgetSlug.SupportTopOffenders]: PaginatedData<SupportTopOffendersData>;
  /** general */
  [WidgetSlug.RealTimeData]: null;
  [WidgetSlug.PendingCustomer]: PendingCustomersResult;
  /** accounting */
  [WidgetSlug.AccountingOverview]: null;
}

export type OrderTotalsResult = ResultDataRow[];
export type OrderTotalByTypeResult = Array<ResultDataRow & { type: OrderType }>;
export type OrderTotalByOriginResult = Array<ResultDataRow & { origin: OrderOrigin }>;

export type PendingCustomersResult = Array<Omit<ResultDataRow, "value">> & Partial<{ [customGroupKey: string]: string }>;

export interface ResultDataRow {
  date: Date;
  count: number;
  value: number;
}

type SupportResultRow = Omit<ResultDataRow, "value">;
export type TotalTicketsResult = SupportResultRow[];
export type TotalTimeSpentResult = Array<SupportResultRow & { totalTime: string }>;
export type TotalTicketsByTypeResult = Array<SupportResultRow & { type: string }>;
export type TotalTicketsByStatusResult = Array<SupportResultRow & { status: SupportTicketStatus }>;

export interface CreditTopOffendersData {
  branchNumber: string;
  branchName: string;
  customerNumber: string;
  customerName: string;
  quantityRequested: number;
  requestedValue: number;
  quantityApproved: number;
  approvedValue: number;
  rejected: number;
  creditScore: number;
  currency: string
}

export interface SupportTopOffendersData {
  branchNumber: string;
  branchName: string;
  customerNumber: string;
  customerName: string;
  totalTickets: number;
  totalUnresolved: number;
  totalResolved: number;
  totalTimeTaken: number;
}

const BASE_URL = `${process.env.REACT_APP_API_URL}/core/dashboards`;

const requestObject: RequestConfig<WidgetDataEndpointConfig> = {
  GET_CREDIT_TOP_OFFENDERS: data => ({
    url: `${BASE_URL}/widget-data/${data.widgetTypeId}`,
    params: {
      filters: data.filters,
      options: data.options
    },
    method: "GET"
  }),
  GET_SUPPORT_TOP_OFFENDERS: data => ({
    url: `${BASE_URL}/widget-data/${data.widgetTypeId}`,
    params: {
      filters: data.filters,
      options: data.options
    },
    method: "GET"
  }),
  GET_ORDER_TOTALS: (data, cache) => ({
    ...cache,
    url: `${BASE_URL}/widget-data/${data.widgetTypeId}`,
    params: {
      filters: data.filters,
      options: data.options
    },
    method: "GET"
  }),
  GET_ORDER_TOTALS_BY_ORIGIN: (data, cache) => ({
    ...cache,
    url: `${BASE_URL}/widget-data/${data.widgetTypeId}`,
    params: {
      filters: data.filters,
      options: data.options
    },
    method: "GET"
  }),
  GET_ORDER_TOTALS_BY_TYPE: (data, cache) => ({
    ...cache,
    url: `${BASE_URL}/widget-data/${data.widgetTypeId}`,
    params: {
      filters: data.filters,
      options: data.options
    },
    method: "GET"
  }),
  GET_TOTAL_TICKETS: (data, cache) => ({
    ...cache,
    url: `${BASE_URL}/widget-data/${data.widgetTypeId}`,
    params: {
      filters: data.filters,
      options: data.options
    },
    method: "GET"
  }),
  GET_TOTAL_TICKETS_BY_STATUS: (data, cache) => ({
    ...cache,
    url: `${BASE_URL}/widget-data/${data.widgetTypeId}`,
    params: {
      filters: data.filters,
      options: data.options
    },
    method: "GET"
  }),
  GET_TOTAL_TICKETS_BY_TYPE: (data, cache) => ({
    ...cache,
    url: `${BASE_URL}/widget-data/${data.widgetTypeId}`,
    params: {
      filters: data.filters,
      options: data.options
    },
    method: "GET"
  }),
  GET_TOTAL_TIME_SPENT: (data, cache) => ({
    ...cache,
    url: `${BASE_URL}/widget-data/${data.widgetTypeId}`,
    params: {
      filters: data.filters,
      options: data.options
    },
    method: "GET"
  }),
  GET_PENDING_CUSTOMER_TOTALS: (data, cache) => ({
    ...cache,
    url: `${BASE_URL}/widget-data/${data.widgetTypeId}`,
    params: {
      filters: data.filters,
      options: data.options
    },
    method: "GET"
  })
};

export const useWidgetDataApi: UseApiRequest<WidgetDataEndpointConfig> = name => useAxios(requestObject[ name ], name);
